import { useEffect, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Mail, Lock, Pencil, Send, Users, Search, RefreshCw, ChevronDown, ChevronRight,
  Bold, Italic, List, Link2, Megaphone, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";

const AUDIENCE_ORDER = ["systeme", "membre", "pro", "organisme", "asso", "club", "admin"];
const MANUAL_AUDIENCES = [
  { value: "membre", label: "Membres" },
  { value: "pro", label: "Partenaires / Pros" },
  { value: "organisme", label: "Organismes & CSE" },
  { value: "association", label: "Associations" },
  { value: "club", label: "Clubs" },
];

/* ------------------------------------------------------------------ */
/* Onglet « E-mails automatiques »                                     */
/* ------------------------------------------------------------------ */
function AutoEmails() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openGroups, setOpenGroups] = useState({ systeme: true, membre: true });
  const [editing, setEditing] = useState(null); // item en cours de personnalisation
  const [subj, setSubj] = useState("");
  const [intro, setIntro] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/emails/catalog");
      setItems(data.items || []);
    } catch { toast.error("Erreur de chargement du catalogue."); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const persist = async (key, body) => {
    const { data } = await api.put(`/admin/emails/config/${key}`, body);
    return data;
  };

  const toggle = async (it, next) => {
    setItems((prev) => prev.map((x) => (x.key === it.key ? { ...x, active: next } : x)));
    try {
      await persist(it.key, { active: next, subject_override: it.subject_override, intro_override: it.intro_override });
      toast.success(next ? "E-mail activé." : "E-mail désactivé.");
    } catch {
      toast.error("Échec de la mise à jour.");
      setItems((prev) => prev.map((x) => (x.key === it.key ? { ...x, active: !next } : x)));
    }
  };

  const openEdit = (it) => { setEditing(it); setSubj(it.subject_override || ""); setIntro(it.intro_override || ""); };

  const saveEdit = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await persist(editing.key, { active: editing.active, subject_override: subj, intro_override: intro });
      setItems((prev) => prev.map((x) => (x.key === editing.key ? { ...x, subject_override: subj.trim(), intro_override: intro.trim() } : x)));
      toast.success("Personnalisation enregistrée.");
      setEditing(null);
    } catch { toast.error("Échec de l'enregistrement."); }
    finally { setSaving(false); }
  };

  const groups = AUDIENCE_ORDER
    .map((aud) => ({ aud, label: (items.find((i) => i.audience === aud) || {}).audience_label || aud, list: items.filter((i) => i.audience === aud) }))
    .filter((g) => g.list.length);

  if (loading) return <div className="text-slate-500 text-sm py-10" data-testid="admin-emails-loading">Chargement…</div>;

  return (
    <div data-testid="admin-emails-auto">
      <p className="text-slate-500 text-sm mb-4">
        Activez ou coupez chaque e-mail automatique et personnalisez son objet et son message d'intro.
        Les <strong>e-mails système</strong> (mot de passe, identifiants, activation…) restent toujours actifs.
      </p>
      <div className="space-y-4">
        {groups.map((g) => {
          const open = openGroups[g.aud];
          return (
            <div key={g.aud} className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid={`email-group-${g.aud}`}>
              <button
                onClick={() => setOpenGroups((p) => ({ ...p, [g.aud]: !p[g.aud] }))}
                className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100"
                data-testid={`email-group-toggle-${g.aud}`}
              >
                <span className="font-semibold text-slate-800 flex items-center gap-2">
                  {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />} {g.label}
                  <span className="text-xs font-normal text-slate-400">({g.list.length})</span>
                </span>
              </button>
              {open && (
                <div className="divide-y divide-slate-100">
                  {g.list.map((it) => (
                    <div key={it.key} className="px-4 py-3 flex flex-wrap items-start gap-3" data-testid={`email-row-${it.key}`}>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-slate-900 text-sm">{it.label}</span>
                          <Badge variant="outline" className="text-[10px] font-normal">{it.frequency}</Badge>
                          {it.critical && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                              <Lock size={10} /> Toujours actif
                            </span>
                          )}
                          {(it.subject_override || it.intro_override) && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-full px-2 py-0.5">
                              <CheckCircle2 size={10} /> Personnalisé
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5">{it.description}</div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Button variant="outline" size="sm" onClick={() => openEdit(it)} data-testid={`email-edit-${it.key}`}>
                          <Pencil size={13} className="mr-1" /> Personnaliser
                        </Button>
                        <Switch
                          checked={it.active}
                          disabled={it.critical}
                          onCheckedChange={(v) => toggle(it, v)}
                          data-testid={`email-switch-${it.key}`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="email-edit-dialog">
          <DialogHeader><DialogTitle>Personnaliser — {editing?.label}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-slate-600">Objet personnalisé (laisser vide pour l'objet par défaut)</label>
              <Input value={subj} onChange={(e) => setSubj(e.target.value)} placeholder="Objet de l'e-mail…" data-testid="email-edit-subject" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600">Message d'intro (ajouté en haut de l'e-mail)</label>
              <Textarea value={intro} onChange={(e) => setIntro(e.target.value)} rows={4} placeholder="Ex. Petit mot de l'équipe PROXIreduc-28…" data-testid="email-edit-intro" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Annuler</Button>
            <Button onClick={saveEdit} disabled={saving} className="bg-[#E05638] hover:bg-[#c94a2f]" data-testid="email-edit-save">
              {saving ? "Enregistrement…" : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Éditeur enrichi simple                                              */
/* ------------------------------------------------------------------ */
function RichEditor({ onChange }) {
  const ref = useRef(null);
  const exec = (cmd) => { document.execCommand(cmd, false, null); ref.current?.focus(); fire(); };
  const addLink = () => {
    const url = window.prompt("Adresse du lien (https://…)");
    if (url) { document.execCommand("createLink", false, url); fire(); }
  };
  const fire = () => onChange(ref.current?.innerHTML || "");
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <div className="flex items-center gap-1 bg-slate-50 border-b border-slate-200 px-2 py-1">
        <button type="button" onClick={() => exec("bold")} className="p-1.5 rounded hover:bg-slate-200" title="Gras" data-testid="rich-bold"><Bold size={14} /></button>
        <button type="button" onClick={() => exec("italic")} className="p-1.5 rounded hover:bg-slate-200" title="Italique" data-testid="rich-italic"><Italic size={14} /></button>
        <button type="button" onClick={() => exec("insertUnorderedList")} className="p-1.5 rounded hover:bg-slate-200" title="Liste" data-testid="rich-list"><List size={14} /></button>
        <button type="button" onClick={addLink} className="p-1.5 rounded hover:bg-slate-200" title="Lien" data-testid="rich-link"><Link2 size={14} /></button>
      </div>
      <div
        ref={ref}
        contentEditable
        onInput={fire}
        className="min-h-[160px] px-3 py-2 text-sm focus:outline-none prose prose-sm max-w-none [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5"
        data-testid="manual-body-editor"
        suppressContentEditableWarning
      />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Onglet « Envoi manuel »                                             */
/* ------------------------------------------------------------------ */
function ManualSend() {
  const [audience, setAudience] = useState("membre");
  const [q, setQ] = useState("");
  const [city, setCity] = useState("");
  const [cities, setCities] = useState([]);
  const [recips, setRecips] = useState([]);
  const [total, setTotal] = useState(0);
  const [audienceTotal, setAudienceTotal] = useState(0);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [scope, setScope] = useState("selected");
  const [includeOptout, setIncludeOptout] = useState(false);
  const [optoutAvailable, setOptoutAvailable] = useState(0);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ audience, q, city, include_optout: includeOptout });
      const { data } = await api.get(`/admin/emails/recipients?${params.toString()}`);
      setRecips(data.items || []);
      setTotal(data.total || 0);
      setAudienceTotal(data.audience_total ?? data.total ?? 0);
      setOptoutAvailable(data.optout_available || 0);
      setTruncated(!!data.truncated);
      setCities(data.cities || []);
    } catch { toast.error("Erreur de chargement des destinataires."); }
    finally { setLoading(false); }
  }, [audience, q, city, includeOptout]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => { setSelected(new Set()); }, [audience, includeOptout]);

  const toggleOne = (em) => {
    setSelected((prev) => { const n = new Set(prev); n.has(em) ? n.delete(em) : n.add(em); return n; });
  };
  const allLoadedSelected = recips.length > 0 && recips.every((r) => selected.has(r.email));
  const toggleAll = () => {
    setSelected((prev) => {
      if (allLoadedSelected) { const n = new Set(prev); recips.forEach((r) => n.delete(r.email)); return n; }
      const n = new Set(prev); recips.forEach((r) => n.add(r.email)); return n;
    });
  };

  const recipientCount = scope === "selected" ? selected.size : scope === "all" ? audienceTotal : total;

  const doSend = async (test) => {
    if (!subject.trim() || subject.trim().length < 2) { toast.error("Ajoutez un objet."); return; }
    const plain = body.replace(/<[^>]+>/g, "").trim();
    if (plain.length < 3) { toast.error("Ajoutez un message."); return; }
    if (!test) {
      if (scope === "selected" && selected.size === 0) { toast.error("Sélectionnez au moins un destinataire."); return; }
      if (!window.confirm(`Envoyer cet e-mail à ${recipientCount} destinataire(s) ?`)) return;
    }
    setSending(true);
    try {
      const payload = {
        audience, subject: subject.trim(), body, scope, q, city,
        emails: scope === "selected" ? Array.from(selected) : [],
        include_optout: includeOptout,
        test: !!test,
      };
      const { data } = await api.post("/admin/emails/send-manual", payload);
      if (test) toast.success(`E-mail de test envoyé à ${data.to}`);
      else toast.success(`Envoi lancé vers ${data.queued} destinataire(s).`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'envoi.");
    } finally { setSending(false); }
  };

  return (
    <div data-testid="admin-emails-manual" className="grid lg:grid-cols-5 gap-5">
      {/* Colonne destinataires */}
      <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl p-4">
        <h3 className="font-semibold text-slate-800 flex items-center gap-2 mb-3"><Users size={16} /> Destinataires</h3>
        <div className="space-y-2">
          <Select value={audience} onValueChange={setAudience}>
            <SelectTrigger data-testid="manual-audience"><SelectValue /></SelectTrigger>
            <SelectContent>
              {MANUAL_AUDIENCES.map((a) => <SelectItem key={a.value} value={a.value} data-testid={`manual-audience-${a.value}`}>{a.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher (nom ou e-mail)…" className="pl-8" data-testid="manual-search" />
          </div>
          <Select value={city || "__all__"} onValueChange={(v) => setCity(v === "__all__" ? "" : v)}>
            <SelectTrigger data-testid="manual-city"><SelectValue placeholder="Toutes les villes" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Toutes les villes</SelectItem>
              {cities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center justify-between mt-3 mb-1">
          <button onClick={toggleAll} className="text-xs font-semibold text-[#E05638] inline-flex items-center gap-1" data-testid="manual-select-all">
            <Checkbox checked={allLoadedSelected} className="mr-1" /> Tout sélectionner
          </button>
          <span className="text-xs text-slate-400">{total} trouvé(s){truncated ? " · 300 affichés" : ""}</span>
        </div>
        <div className="border border-slate-100 rounded-lg max-h-72 overflow-y-auto divide-y divide-slate-50" data-testid="manual-recipient-list">
          {loading ? <div className="p-4 text-sm text-slate-400">Chargement…</div> :
           recips.length === 0 ? <div className="p-4 text-sm text-slate-400">Aucun destinataire.</div> :
           recips.map((r) => (
             <label key={r.email} className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 cursor-pointer" data-testid={`manual-recipient-${r.email}`}>
               <Checkbox checked={selected.has(r.email)} onCheckedChange={() => toggleOne(r.email)} />
               <span className="min-w-0 flex-1">
                 <span className="block text-sm text-slate-800 truncate">{r.name}</span>
                 <span className="block text-xs text-slate-400 truncate">{r.email}{r.city ? ` · ${r.city}` : ""}</span>
               </span>
             </label>
           ))}
        </div>
        <Button variant="ghost" size="sm" onClick={load} className="mt-2 text-slate-500" data-testid="manual-reload">
          <RefreshCw size={13} className="mr-1" /> Actualiser
        </Button>
      </div>

      {/* Colonne composition */}
      <div className="lg:col-span-3 bg-white border border-slate-200 rounded-2xl p-4">
        <h3 className="font-semibold text-slate-800 flex items-center gap-2 mb-3"><Megaphone size={16} /> Composer l'e-mail</h3>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-600">Cible de l'envoi</label>
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger data-testid="manual-scope"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="selected" data-testid="manual-scope-selected">Destinataires cochés ({selected.size})</SelectItem>
                <SelectItem value="filtered" data-testid="manual-scope-filtered">Tous les résultats filtrés ({total})</SelectItem>
                <SelectItem value="all" data-testid="manual-scope-all">Tout le public sélectionné ({audienceTotal})</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {optoutAvailable > 0 && (
            <label className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 cursor-pointer" data-testid="manual-include-optout-wrap">
              <Checkbox checked={includeOptout} onCheckedChange={(v) => setIncludeOptout(!!v)} data-testid="manual-include-optout" className="mt-0.5" />
              <span className="text-xs text-amber-800">
                Inclure aussi les <strong>{optoutAvailable}</strong> inscrit(s) désabonné(s) des alertes.
                <span className="block text-amber-600">À réserver aux communications importantes (RGPD).</span>
              </span>
            </label>
          )}
          <div>
            <label className="text-xs font-semibold text-slate-600">Objet</label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Objet de l'e-mail…" data-testid="manual-subject" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">Message</label>
            <RichEditor onChange={setBody} />
          </div>
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <Button variant="outline" onClick={() => doSend(true)} disabled={sending} data-testid="manual-send-test">
              <Mail size={14} className="mr-1" /> M'envoyer un test
            </Button>
            <Button onClick={() => doSend(false)} disabled={sending} className="bg-[#E05638] hover:bg-[#c94a2f]" data-testid="manual-send">
              <Send size={14} className="mr-1" /> {sending ? "Envoi…" : `Envoyer (${recipientCount})`}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
export default function AdminEmails() {
  const [tab, setTab] = useState("auto");
  return (
    <div data-testid="admin-emails-page">
      <div className="mb-5">
        <h1 className="font-heading text-2xl md:text-3xl font-bold flex items-center gap-2" data-testid="admin-emails-title">
          <Mail className="text-[#E05638]" size={26} /> E-mails & Communications
        </h1>
        <p className="text-slate-500 text-xs mt-1">
          Paramétrez tous les e-mails automatiques de la plateforme et envoyez des e-mails ciblés.
        </p>
      </div>

      <div className="inline-flex rounded-xl bg-white border border-slate-200 p-1 gap-1 mb-5" data-testid="admin-emails-tabs">
        <button onClick={() => setTab("auto")} data-testid="admin-emails-tab-auto"
          className={`px-4 py-1.5 rounded-lg text-sm font-semibold ${tab === "auto" ? "bg-[#E05638] text-white" : "text-slate-600 hover:bg-slate-100"}`}>
          E-mails automatiques
        </button>
        <button onClick={() => setTab("manual")} data-testid="admin-emails-tab-manual"
          className={`px-4 py-1.5 rounded-lg text-sm font-semibold ${tab === "manual" ? "bg-[#E05638] text-white" : "text-slate-600 hover:bg-slate-100"}`}>
          Envoi manuel
        </button>
      </div>

      {tab === "auto" ? <AutoEmails /> : <ManualSend />}
    </div>
  );
}
